/**
 * Steps a shopper through the merchant's finder questions one at a time and reveals the result
 * whose answer values they matched.
 *
 * Everything this element does is subtractive — the section renders every question and every
 * result as readable content, and the element hides what is not currently relevant. If the script
 * never runs the page is still usable, which is why nothing here is created from scratch and no
 * user-facing string is written in this file.
 */
class ProductFinder extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-finder-step]'));
    this.results = Array.from(this.querySelectorAll('[data-finder-result]'));
    if (this.steps.length === 0) return;

    this.progress = this.querySelector('[data-finder-progress]');
    this.progressLabel = this.querySelector('[data-finder-progress-label]');
    this.progressBar = this.querySelector('[data-finder-progress-bar]');
    this.announcer = this.querySelector('[data-finder-announcer]');
    this.resultsTitle = this.querySelector('[data-finder-results-title]');
    this.fallback = this.querySelector('[data-finder-fallback]');
    this.backButton = this.querySelector('[data-finder-back]');
    this.restartButton = this.querySelector('[data-finder-restart]');

    // Re-picking the answer you already gave fires no change event, so a shopper who steps back
    // and keeps their answer would be stranded. Click covers that; advancing twice is harmless.
    this.addEventListener('change', this.onAnswer.bind(this));
    this.addEventListener('click', this.onAnswer.bind(this));
    this.backButton?.addEventListener('click', this.back.bind(this));
    this.restartButton?.addEventListener('click', this.restart.bind(this));

    this.restart();
  }

  restart() {
    this.querySelectorAll('[data-finder-input]:checked').forEach((input) => {
      input.checked = false;
    });

    this.index = 0;
    this.render();

    // Focus only belongs to the finder once the shopper has asked for a step, never on load.
    if (this.hasStarted) this.focusStep();
    this.hasStarted = true;
  }

  onAnswer(event) {
    if (!event.target.matches('[data-finder-input]') || !event.target.checked) return;

    this.index = this.steps.indexOf(event.target.closest('[data-finder-step]')) + 1;
    this.render();
    this.focusStep();
  }

  back() {
    if (this.index === 0) return;

    this.index -= 1;
    this.render();
    this.focusStep();
  }

  /** Values the shopper has picked so far, in question order. */
  get answers() {
    return this.steps
      .slice(0, this.index)
      .map((step) => step.querySelector('[data-finder-input]:checked')?.value.trim().toLowerCase())
      .filter(Boolean);
  }

  get isComplete() {
    return this.index >= this.steps.length;
  }

  render() {
    this.steps.forEach((step, index) => {
      step.hidden = index !== this.index;
    });

    if (this.backButton) this.backButton.hidden = this.index === 0;
    if (this.restartButton) this.restartButton.hidden = this.index === 0;
    if (this.progress) this.progress.hidden = this.isComplete;

    this.renderProgress();
    this.renderResults();
  }

  renderProgress() {
    const total = this.steps.length;
    const current = Math.min(this.index + 1, total);
    const template = this.dataset.stepTemplate || '';
    const label = template.replace('[current]', current).replace('[total]', total);

    if (this.progressLabel) this.progressLabel.textContent = label;
    if (this.progressBar) this.progressBar.style.width = `${(this.index / total) * 100}%`;

    // Announce the step rather than the question — screen readers already read the fieldset
    // legend when focus lands on it, so repeating it here would double up.
    if (this.announcer) this.announcer.textContent = this.isComplete ? '' : label;
  }

  renderResults() {
    const match = this.isComplete ? this.bestResult() : null;

    this.results.forEach((result) => {
      result.hidden = result !== match;
    });

    if (this.resultsTitle) {
      this.resultsTitle.hidden = !this.isComplete;
      this.resultsTitle.textContent = this.resultsTitle.dataset.completeLabel;
    }

    if (this.fallback) this.fallback.hidden = !this.isComplete || match !== null;
  }

  /**
   * A result is eligible when every value it asks for was answered; ties go to the result asking
   * for the most values, so a specific combination always beats a catch-all.
   */
  bestResult() {
    const answers = this.answers;
    let best = null;
    let bestScore = -1;

    this.results.forEach((result) => {
      const required = (result.dataset.match || '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);

      if (!required.every((value) => answers.includes(value))) return;

      if (required.length > bestScore) {
        best = result;
        bestScore = required.length;
      }
    });

    return best;
  }

  focusStep() {
    const target = this.isComplete
      ? this.querySelector('[data-finder-result]:not([hidden]), [data-finder-fallback]:not([hidden])')
      : this.steps[this.index];

    target?.focus();
  }
}

customElements.define('product-finder', ProductFinder);
